"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  getBaseNavItems,
  getGuestNavItems,
  mobileGhostButtonClass,
  mobileNavLinkClass,
  mobilePrimaryLinkClass,
} from "./navigation";
import type { SiteLocale } from "@/lib/i18n/locales";
import { localizeInternalHref } from "@/lib/i18n/paths";
import { getUiDictionary } from "@/lib/i18n/ui";
import LanguageSwitcher from "./language-switcher";

type MobileMenuProps = {
  status: "loading" | "authenticated" | "unauthenticated";
  locale: SiteLocale;
  isAuthenticated: boolean;
  isAdmin: boolean;
  userName: string;
  userImage: string | null;
  onSignOut: () => void;
};

export default function MobileMenu({
  status,
  locale,
  isAuthenticated,
  isAdmin,
  userName,
  userImage,
  onSignOut,
}: MobileMenuProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const t = getUiDictionary(locale);
  const baseNavItems = getBaseNavItems(locale);
  const guestNavItems = getGuestNavItems(locale);
  const myCasesHref = localizeInternalHref("/mis-expedientes", locale);
  const aiAssistantHref = localizeInternalHref("/comprueba-tu-tramite-ia", locale);
  const profileHref = localizeInternalHref("/perfil", locale);

  const trigger = useRef<HTMLButtonElement>(null);
  const mobileNav = useRef<HTMLDivElement>(null);

  // close the mobile menu on click outside
  const clickHandler = useCallback(({ target }: { target: EventTarget | null }): void => {
    if (!mobileNav.current || !trigger.current) return;
    if (!mobileNavOpen || mobileNav.current.contains(target as Node) || trigger.current.contains(target as Node)) return;
    setMobileNavOpen(false);
  }, [mobileNavOpen]);

  useEffect(() => {
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [clickHandler]);

  // close the mobile menu if the esc key is pressed
  const keyHandler = useCallback(({ keyCode }: { keyCode: number }): void => {
    if (!mobileNavOpen || keyCode !== 27) return;
    setMobileNavOpen(false);
  }, [mobileNavOpen]);

  useEffect(() => {
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [keyHandler]);

  return (
    <div className="md:hidden ml-2">
      <div className="flex items-center">
        <button
          ref={trigger}
          className={`hamburger rounded-md p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cream-300 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 ${mobileNavOpen ? 'active' : ''}`}
          aria-controls="mobile-nav"
          aria-expanded={mobileNavOpen}
          aria-label={mobileNavOpen ? t.nav.closeMenu : t.nav.openMenu}
          onClick={() => setMobileNavOpen(prev => !prev)}
        >
          <span className="sr-only">{t.nav.menuSrOnly}</span>
          <svg
            className="h-6 w-6 fill-current text-gray-100 transition duration-150 ease-in-out hover:text-cream-200"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect y="4" width="24" height="2" rx="1" />
            <rect y="11" width="24" height="2" rx="1" />
            <rect y="18" width="24" height="2" rx="1" />
          </svg>
        </button>
      </div>

      <nav
        id="mobile-nav"
        ref={mobileNav}
        className="absolute left-0 top-full z-20 w-full overflow-hidden px-4 transition-all duration-300 ease-in-out sm:px-6"
        style={mobileNavOpen ? { maxHeight: mobileNav.current?.scrollHeight, opacity: 1 } : { maxHeight: 0, opacity: 0.8 }}
      >
        <ul className="space-y-1 rounded-b-xl border border-gray-700 bg-gray-900/95 p-4 shadow-xl">
          <li>
            <LanguageSwitcher className="w-full" variant="inline" />
          </li>

          {baseNavItems.map((item) => (
            <li key={item.href}>
              <Link href={item.href} className={mobileNavLinkClass} onClick={() => setMobileNavOpen(false)}>
                {item.label}
              </Link>
            </li>
          ))}

          {status === "loading" && (
            <li className="px-4 py-3 text-center text-sm text-gray-300">{t.nav.loading}</li>
          )}

          {status !== "loading" && isAuthenticated && (
            <>
              {isAdmin && (
                <li>
                  <Link
                    href="/admin"
                    className="flex w-full items-center justify-center rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm font-bold text-amber-300 transition-colors hover:bg-amber-500/20"
                    onClick={() => setMobileNavOpen(false)}
                  >
                    ⚙️ Admin
                  </Link>
                </li>
              )}
              <li>
                <Link
                  href={myCasesHref}
                  className={mobileNavLinkClass}
                  onClick={() => setMobileNavOpen(false)}
                >
                  {t.nav.myCases}
                </Link>
              </li>
              <li>
                <Link
                  href={aiAssistantHref}
                  className={mobileNavLinkClass}
                  onClick={() => setMobileNavOpen(false)}
                >
                  {t.nav.aiAssistant}
                </Link>
              </li>
              <li>
                <Link
                  href={profileHref}
                  className={mobileNavLinkClass}
                  onClick={() => setMobileNavOpen(false)}
                >
                  <span className="inline-flex items-center gap-2">
                    {userName}
                    {userImage && (
                      <Image
                        src={userImage}
                        width={24}
                        height={24}
                        sizes="24px"
                        alt="Avatar del usuario"
                        className="rounded-full border border-gray-500"
                      />
                    )}
                  </span>
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  className={mobileGhostButtonClass}
                  onClick={() => {
                    setMobileNavOpen(false);
                    onSignOut();
                  }}
                >
                  {t.nav.signOut}
                </button>
              </li>
            </>
          )}

          {status !== "loading" && !isAuthenticated && (
            <>
              <li>
                <Link
                  href={guestNavItems[0].href}
                  className={mobileNavLinkClass}
                  onClick={() => setMobileNavOpen(false)}
                >
                  {guestNavItems[0].label}
                </Link>
              </li>
              <li>
                <Link
                  href={guestNavItems[1].href}
                  className={mobilePrimaryLinkClass}
                  onClick={() => setMobileNavOpen(false)}
                >
                  {guestNavItems[1].label}
                </Link>
              </li>
            </>
          )}
        </ul>
      </nav>
    </div>
  )
}
